import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Switch, InputNumber,
  Space, Tag, Tooltip, message, Card, Row, Col, Statistic, DatePicker,
  Popconfirm, Badge, Typography, Divider, Empty
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  SearchOutlined, CopyOutlined, ThunderboltOutlined, FilterOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import { mockRuleApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;
const { Option } = Select;

const CHANNELS = [
  'CALLPAY', 'CVPAY', 'SGB', 'EQUALS_MONEY', 'BANKING_CIRCLE',
  'PYVIO', 'KORAPAY', 'BITSO', 'LEANTECH', 'BANKFRICK',
  'CHECKOUT', 'PAGSMILE'
];

const SWIMLANES = Array.from({ length: 30 }, (_, i) => `pfb${i + 1}`);

const STATUS_COLORS = { 200: 'green', 400: 'orange', 500: 'red', 201: 'cyan', 204: 'blue' };

/** 把 "$.amount=100;$.currency=ZAR" 解析成 [{field:'amount', value:'100'}, {field:'currency', value:'ZAR'}] */
const parseCondition = (str) => {
  if (!str) return [];
  return str.split(';').filter(Boolean).map(c => {
    const eq = c.indexOf('=');
    if (eq <= 0) return null;
    const field = c.substring(0, eq).trim().replace(/^\$\./, '');
    const value = c.substring(eq + 1).trim();
    return { field, value };
  }).filter(Boolean);
};

/** 把 [{field:'amount', value:'100'}] 序列化成 "$.amount=100" */
const serializeCondition = (arr) => {
  if (!arr || !arr.length) return '';
  return arr.filter(c => c.field && c.value !== undefined && c.value !== '').map(c => `$.${c.field}=${c.value}`).join(';');
};

/** 可视化条件编辑器：每行一个 field = value */
function RequestConditionBuilder({ value, onChange }) {
  const [rows, setRows] = useState(() => {
    const parsed = parseCondition(value);
    return parsed.length ? parsed : [{ field: '', value: '' }];
  });

  useEffect(() => {
    const parsed = parseCondition(value);
    if (parsed.length) {
      setRows(parsed);
    }
  }, [value]);

  const syncToForm = (newRows) => {
    setRows(newRows);
    onChange(serializeCondition(newRows));
  };

  const setRow = (idx, key, val) => {
    const next = [...rows];
    next[idx] = { ...next[idx], [key]: val };
    syncToForm(next);
  };

  const addRow = () => syncToForm([...rows, { field: '', value: '' }]);
  const removeRow = (idx) => syncToForm(rows.filter((_, i) => i !== idx));

  return (
    <div style={{ background: '#fafafa', borderRadius: 6, padding: '12px 12px 8px' }}>
      {rows.map((row, idx) => (
        <Row key={idx} gutter={8} style={{ marginBottom: 8 }} align="middle">
          <Col flex="auto">
            <Input
              placeholder="字段路径，如 amount 或 source.type"
              value={row.field}
              onChange={e => setRow(idx, 'field', e.target.value)}
              addonBefore="$."
            />
          </Col>
          <Col flex="24px" style={{ textAlign: 'center', fontWeight: 600 }}>=</Col>
          <Col flex="auto">
            <Input
              placeholder="期望值，如 100 或 ZAR"
              value={row.value}
              onChange={e => setRow(idx, 'value', e.target.value)}
            />
          </Col>
          <Col flex="32px">
            {rows.length > 1 ? (
              <MinusCircleOutlined style={{ color: '#ff4d4f', fontSize: 16, cursor: 'pointer' }} onClick={() => removeRow(idx)} />
            ) : <span style={{ display: 'inline-block', width: 16 }} />}
          </Col>
        </Row>
      ))}
      <Button type="dashed" size="small" block icon={<PlusOutlined />} onClick={addRow}>
        添加条件
      </Button>
      <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
        多条件为 AND 关系，嵌套字段用 . 分隔（如 source.type）
      </Text>
    </div>
  );
}

export default function MockRuleManagement() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [filterChannel, setFilterChannel] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const { t } = useLanguage();

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await mockRuleApi.list(filterChannel);
      setRules(res.data?.data || []);
    } catch (e) {
      message.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [filterChannel]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleCreate = () => {
    setEditingRule(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true, httpStatus: 200, priority: 0 });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRule(record);
    form.setFieldsValue({
      ...record,
      expireTime: record.expireTime ? dayjs(record.expireTime) : null,
    });
    setModalVisible(true);
  };

  const handleDuplicate = (record) => {
    setEditingRule(null);
    form.setFieldsValue({
      ...record,
      id: undefined,
      description: `${record.description} (copy)`,
      expireTime: record.expireTime ? dayjs(record.expireTime) : null,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (values.expireTime) {
        values.expireTime = values.expireTime.format('YYYY-MM-DDTHH:mm:ss');
      }
      values.creator = localStorage.getItem('username') || 'admin';

      if (editingRule) {
        await mockRuleApi.update(editingRule.id, values);
        message.success('Rule updated');
      } else {
        await mockRuleApi.create(values);
        message.success('Rule created');
      }
      setModalVisible(false);
      fetchRules();
    } catch (e) {
      if (e.errorFields) return;
      message.error('Operation failed');
    }
  };

  const handleDelete = async (id) => {
    await mockRuleApi.delete(id);
    message.success('Rule deleted');
    fetchRules();
  };

  const handleToggle = async (record) => {
    await mockRuleApi.toggle(record.id);
    message.success(record.enabled ? 'Disabled' : 'Enabled');
    fetchRules();
  };

  const handleRefreshCache = async () => {
    await mockRuleApi.refreshCache(filterChannel);
    message.success('Cache refreshed');
  };

  // 过滤
  const filteredRules = rules.filter(r => {
    if (searchText) {
      const s = searchText.toLowerCase();
      return r.pathPattern?.toLowerCase().includes(s)
        || r.description?.toLowerCase().includes(s)
        || r.mockResponse?.toLowerCase().includes(s);
    }
    return true;
  });

  // 统计
  const stats = {
    total: rules.length,
    enabled: rules.filter(r => r.enabled).length,
    disabled: rules.filter(r => !r.enabled).length,
    channels: [...new Set(rules.map(r => r.channel))].length,
  };

  const columns = [
    {
      title: t('channel'),
      dataIndex: 'channel',
      width: 130,
      filters: CHANNELS.map(c => ({ text: c, value: c })),
      onFilter: (value, record) => record.channel === value,
      render: (ch) => (
        <Tag color="blue" style={{ fontWeight: 600, fontSize: 12 }}>{ch}</Tag>
      ),
    },
    {
      title: '🏊 Swimlane',
      dataIndex: 'swimlane',
      width: 110,
      filters: [{ text: 'All Lanes', value: '' }, ...SWIMLANES.map(s => ({ text: s, value: s }))],
      onFilter: (value, record) => (record.swimlane || '') === value,
      render: (val) => val ? <Tag color="purple">{val}</Tag> : <Text type="secondary">All</Text>,
    },
    {
      title: t('mockRulePathPattern'),
      dataIndex: 'pathPattern',
      width: 220,
      render: (text) => (
        <Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>{text}</Text>
      ),
    },
    {
      title: t('mockRuleDescription'),
      dataIndex: 'description',
      width: 200,
      ellipsis: true,
      render: (text) => <Text type="secondary">{text || '-'}</Text>,
    },
    {
      title: '🎯 Condition',
      dataIndex: 'requestCondition',
      width: 180,
      ellipsis: true,
      render: (val) => {
        if (!val) return <Text type="secondary">-</Text>;
        const conditions = parseCondition(val);
        return (
          <Tooltip title={conditions.map(c => `${c.field} = ${c.value}`).join('\n')}>
            <Space size={2} wrap>
              {conditions.map((c, i) => (
                <Tag key={i} color="geekblue" style={{ fontSize: 11 }}>{c.field}={c.value}</Tag>
              ))}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: t('status'),
      dataIndex: 'httpStatus',
      width: 80,
      align: 'center',
      render: (s) => <Tag color={STATUS_COLORS[s] || 'default'}>{s}</Tag>,
    },
    {
      title: t('mockRulePriority'),
      dataIndex: 'priority',
      width: 80,
      align: 'center',
      sorter: (a, b) => b.priority - a.priority,
    },
    {
      title: t('enable'),
      dataIndex: 'enabled',
      width: 90,
      align: 'center',
      filters: [{ text: 'Enabled', value: true }, { text: 'Disabled', value: false }],
      onFilter: (value, record) => record.enabled === value,
      render: (enabled, record) => (
        <Switch
          checked={enabled}
          onChange={() => handleToggle(record)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
          size="small"
        />
      ),
    },
    {
      title: t('mockRuleExpireTime'),
      dataIndex: 'expireTime',
      width: 140,
      render: (t_val) => {
        if (!t_val) return <Text type="secondary">{t('mockRuleNeverExpire')}</Text>;
        const expired = dayjs(t_val).isBefore(dayjs());
        return (
          <Tooltip title={t_val}>
            <Badge status={expired ? 'error' : 'processing'} />
            <Text type={expired ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
              {dayjs(t_val).format('MM-DD HH:mm')}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: t('mockRuleResponsePreview'),
      dataIndex: 'mockResponse',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={<pre style={{ maxHeight: 300, overflow: 'auto', fontSize: 11, margin: 0 }}>{
          (() => { try { return JSON.stringify(JSON.parse(text), null, 2); } catch { return text; } })()
        }</pre>} overlayStyle={{ maxWidth: 500 }}>
          <Text code style={{ fontSize: 11, cursor: 'pointer' }}>
            {text?.length > 60 ? text.substring(0, 60) + '...' : text}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: t('mockRuleActions'),
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title={t('edit')}>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title={t('mockRuleDuplicate')}>
            <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => handleDuplicate(record)} />
          </Tooltip>
          <Popconfirm title={t('mockRuleConfirmDelete')} onConfirm={() => handleDelete(record.id)} okText={t('yes')} cancelText={t('no')}>
            <Tooltip title={t('delete')}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card size="small" style={{ borderRadius: 8 }}>
            <Statistic title={t('mockRuleTotal')} value={stats.total} prefix={<ThunderboltOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ borderRadius: 8 }}>
            <Statistic title={t('mockRuleEnabled')} value={stats.enabled} valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ borderRadius: 8 }}>
            <Statistic title={t('mockRuleDisabled')} value={stats.disabled} valueStyle={{ color: '#ff4d4f' }}
              prefix={<CloseCircleOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ borderRadius: 8 }}>
            <Statistic title={t('mockRuleChannels')} value={stats.channels} prefix={<FilterOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* 工具栏 */}
      <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Select
                allowClear
                placeholder={t('mockRuleFilterChannel')}
                style={{ width: 180 }}
                value={filterChannel}
                onChange={(v) => setFilterChannel(v || null)}
                showSearch
                optionFilterProp="label"
                options={CHANNELS.map(c => ({ label: c, value: c }))}
              />
              <Input
                placeholder={t('mockRuleSearchPlaceholder')}
                prefix={<SearchOutlined />}
                style={{ width: 260 }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                {t('mockRuleNewRule')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 表格 */}
      <Card size="small" style={{ borderRadius: 8 }}>
        <Table
          columns={columns}
          dataSource={filteredRules}
          rowKey="id"
          loading={loading}
          size="middle"
          scroll={{ x: 1600 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => {
              const fn = t('mockRuleTotalRules');
              return typeof fn === 'function' ? fn(total) : `Total ${total} rules`;
            },
            defaultPageSize: 20,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          rowClassName={(record) => !record.enabled ? 'mock-rule-disabled' : ''}
          locale={{ emptyText: <Empty description={t('mockRuleNoData')} /> }}
        />
      </Card>

      {/* 新建/编辑弹窗 */}
      <Modal
        title={editingRule ? t('mockRuleEditRule') : t('mockRuleCreateRule')}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={780}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="channel" label={t('channel')} rules={[{ required: true }]}>
                <Select placeholder="Select channel" showSearch optionFilterProp="label"
                  options={CHANNELS.map(c => ({ label: c, value: c }))} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="swimlane" label="🏊 Swimlane">
                <Select placeholder="All lanes" allowClear showSearch
                  options={SWIMLANES.map(s => ({ label: s, value: s }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="pathPattern" label={t('mockRulePathPattern')} rules={[{ required: true }]}>
                <Input placeholder=".*payout.*queue.* 或 /v2/orders/quote" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label={t('mockRuleDescription')}>
            <Input placeholder="e.g. Mock CallPay queue payout success" />
          </Form.Item>
          <Form.Item name="requestCondition" label="🎯 Request Condition（请求字段匹配条件，全部满足才命中）">
            <RequestConditionBuilder />
          </Form.Item>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="httpStatus" label={t('mockRuleHttpStatus')} rules={[{ required: true }]}>
                <Select options={[
                  { label: '200 OK', value: 200 },
                  { label: '201 Created', value: 201 },
                  { label: '400 Bad Request', value: 400 },
                  { label: '500 Server Error', value: 500 },
                ]} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="priority" label={t('mockRulePriority')}>
                <InputNumber min={0} max={999} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="expireTime" label={t('mockRuleExpireTime')}>
                <DatePicker showTime style={{ width: '100%' }} placeholder={t('mockRuleNeverExpire')} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="enabled" label={t('enable')} valuePropName="checked">
                <Switch checkedChildren="ON" unCheckedChildren="OFF" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="mockResponse" label={t('mockRuleMockResponse')} rules={[{ required: true }]}>
            <TextArea rows={8} placeholder='{"success": true, "message": "OK", "data": {...}}'
              style={{ fontFamily: 'monospace', fontSize: 12 }} />
          </Form.Item>
        </Form>
      </Modal>

      <style>{`
        .mock-rule-disabled td { opacity: 0.5; }
      `}</style>
    </div>
  );
}
